import axios from 'axios';
import { getToken, getRefreshToken, removeToken, setToken, setRefreshToken } from '../../utils/tokenStorage';
import { PYTHON_API_URL } from '../../config';

class PythonApiClient {
  constructor() {
    this.client = axios.create({
      baseURL: PYTHON_API_URL,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.setupInterceptors();
  }

  setupInterceptors() {
    // Request interceptor
    this.client.interceptors.request.use(
      (config) => {
        const token = getToken();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor for token refresh
    this.client.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;
        const isPublicAuthRequest = originalRequest?.url?.includes('/auth/');
        const refreshToken = getRefreshToken();

        // Login, signup and other public auth endpoints use 401 for expected
        // validation failures. Retrying those responses as expired sessions
        // causes a hard redirect that erases the form's inline error.
        if (
          error.response?.status === 401 &&
          originalRequest &&
          !isPublicAuthRequest &&
          refreshToken &&
          !originalRequest._retry
        ) {
          originalRequest._retry = true;

          try {
            const newTokens = await this.refreshToken();
            setToken(newTokens.authToken);
            setRefreshToken(newTokens.refreshToken);
            
            originalRequest.headers.Authorization = `Bearer ${newTokens.authToken}`;
            return this.client(originalRequest);
          } catch (refreshError) {
            this.logout();
            return Promise.reject(refreshError);
          }
        }

        return Promise.reject(error);
      }
    );
  }

  async refreshToken() {
    const refreshToken = getRefreshToken();
    if (!refreshToken) {
      throw new Error('No refresh token available');
    }

    try {
      const response = await axios.post(`${PYTHON_API_URL}/auth/refresh/`, {
        refreshToken,
      });
      return response.data;
    } catch (error) {
      if (error.response?.status === 401) {
        this.logout();
      }
      throw error;
    }
  }

  logout() {
    removeToken();
    window.location.href = '/auth/login';
  }

  // API methods
  async get(url, config = {}) {
    const response = await this.client.get(url, config);
    return response.data;
  }

  async post(url, data = {}, config = {}) {
    const response = await this.client.post(url, data, config);
    return response.data;
  }

  async put(url, data = {}, config = {}) {
    const response = await this.client.put(url, data, config);
    return response.data;
  }

  async delete(url, config = {}) {
    const response = await this.client.delete(url, config);
    return response.data;
  }
}

export const pythonApiClient = new PythonApiClient();